// Percorso file: src/utils/statistiche-divise.js
//
// IL REPORT DELLE DIVISE (UNIFORMS-UI / Fetta STATS, 19 settembre 2026):
// la rendicontazione per capo, per taglia e per squadra che il pulsante
// «Stats» della pagina Uniform Distribution apre al solo SUPERADMIN.
//
// ⚠️ MODULO PURO, e deve restarlo: nessun React, nessun hook, nessuna
// chiamata di rete. Riceve i dati che la pagina ha GIÀ — l'elenco di
// `GET /users` (ogni persona con le sue `contexts` e le sue consegne: la
// mappa per anno `uniformsSeasons` e il piatto `uniforms`, che è il 2026
// storico), il catalogo dell'anno guardato e la lista delle taglie — e
// rende una struttura di soli numeri. Il componente che la disegna non
// calcola nulla: chiama `reportDivise` e incolonna. Così il grosso si prova
// con `node`, su persone e cataloghi inventati.
//
// ⚠️ NON SI INVENTA NULLA: ogni pezzo viene dalle primitive esistenti.
//   - chi è un GIOCATORE dell'anno: `tessereDelSoggetto` con i default del
//     modulo (solo ACTIVE, tessera senz'anno esclusa, nessuna amministrativa)
//     e `canonicalRole === "PLAYER"` — lo stesso predicato della pagina
//     (`RUOLI_IN_ELENCO`), ristretto ai soli PLAYER (decisione del
//     committente: i coach sono fuori dal report);
//   - la SQUADRA del giocatore: `primaSquadraDellaTessera` (una squadra per
//     player, l'invariante del club);
//   - la SPETTANZA: `tipiDivisaSpettanti` chiamata sulla SOLA tessera PLAYER,
//     così chi è player E coach entra per la sua parte di giocatore e basta
//     (il ramo ruolo non si accende senza una tessera COACH);
//   - i CAPI: `capiAttivi(capiDelCatalogo(config.items))`, `capoHaTaglia` —
//     le colonne della pagina, né più né meno;
//   - le CONSEGNE dell'anno: `consegneDellAnnata(utente, anno)` (sede unica,
//     utils/uniforms.js; cantiere UNIFORMS-ANNO/Fetta 3) — la fetta
//     `uniformsSeasons[anno]` se c'è, altrimenti solo per il 2026 il piatto,
//     altrimenti nessuna. Così il report del 2027 conta il 2027, non il 2026
//     (chiude UNIF3-12 §7.2). Il piatto non si legge mai direttamente;
//   - «CONSEGNATO» = `status === "delivered"`, il valore dei documenti
//     (`ETICHETTE_STATO`, utils/uniforms.js). Tutto il resto — not_delivered,
//     no, cella assente — è «non consegnato» ai fini del conteggio.
//
// LA STIMA (decisione del capomastro, §1-bis dell'incarico UNIF3-12): per la
// parte NON consegnata di ogni (squadra × capo) si stima come si
// distribuirebbero le taglie, in proporzione alle taglie GIÀ consegnate di
// quello stesso (squadra × capo), con arrotondamento «largest remainder»
// (nessun pezzo si perde: la somma delle taglie stimate fa esattamente il
// numero dei non consegnati). Regole ferme, tutte nel risultato:
//   - `stimata: true` marca ogni ripartizione stimata: chi disegna DEVE
//     etichettarla «est.» e distinguerla dai numeri veri;
//   - SOGLIA (scelta dell'esecutore, la più prudente proposta dall'incarico):
//     serve ALMENO UNA consegna con taglia NOTA in quel (squadra × capo);
//     altrimenti `disponibile: false` e nessun numero, invece di inventare;
//   - la taglia VUOTA fra i consegnati («taglia non indicata») NON entra nella
//     base della proporzione: non è una taglia; e nemmeno una taglia fuori
//     dalla lista (`ALTRA_TAGLIA`), che è un'anomalia del dato, non una misura;
//   - un capo SENZA taglia (`hasSize:false`) non ha nulla da stimare: tutto
//     ciò che si consegna è «(no size)», e il «da ordinare» è il totale che
//     spetta — un numero ESATTO, non una stima (`stimata: false`);
//   - il totale generale per capo è la SOMMA delle stime per squadra (non una
//     proporzione globale): così la sezione «per squadra» e quella «per capo»
//     dicono le stesse cose, e ciò che nessuna squadra sa stimare resta a
//     verbale in `nonStimabili` invece di sparire in una media.
import { canonicalRole } from "./roleUtils";
import { tessereDelSoggetto, primaSquadraDellaTessera } from "./squadre";
import {
  capiDelCatalogo,
  capiAttivi,
  capoHaTaglia,
  tipiDivisaSpettanti,
  consegneDellAnnata
} from "./uniforms";

/** La chiave della «taglia non indicata»: la stringa vuota, cioè il valore
 *  del segnaposto «Size» di una tendina taglia mai toccata. Dal D9A non è
 *  più una voce della lista `sizes` della pagina; se ci fosse, il filtro di
 *  `reportDivise` la toglierebbe comunque. */
export const SENZA_TAGLIA = "";

/** La chiave di una taglia consegnata che NON sta nella lista (dato storico
 *  con una grafia diversa): si conta, si mostra a parte, non si stima. */
export const ALTRA_TAGLIA = "__other__";

/** Il valore di `status` che vale «consegnato»: quello dei documenti. */
export const STATO_CONSEGNATO = "delivered";

/** Il metodo di arrotondamento della stima, dichiarato nel risultato. */
export const METODO_ARROTONDAMENTO = "largest-remainder";

const contatore = (chiavi) => {
  const c = {};
  chiavi.forEach((k) => {
    c[k] = 0;
  });
  return c;
};

const sommaIn = (destinazione, sorgente) => {
  Object.keys(sorgente || {}).forEach((k) => {
    destinazione[k] = (destinazione[k] || 0) + (sorgente[k] || 0);
  });
  return destinazione;
};

const senzaZeri = (mappa) => {
  const pulita = {};
  Object.keys(mappa || {}).forEach((k) => {
    if (mappa[k] > 0) pulita[k] = mappa[k];
  });
  return pulita;
};

/**
 * La tessera PLAYER valida dell'anno, o `null`. Stesso predicato della pagina
 * (`tessereDelSoggetto` coi default + `canonicalRole`), ristretto a PLAYER;
 * se ce ne fossero due si prende la prima, come fa `tipiDivisaSpettanti`.
 */
export const tesseraGiocatoreDellAnno = (contexts, anno) =>
  tessereDelSoggetto(contexts, anno).find(
    (ctx) => canonicalRole(ctx?.role) === "PLAYER"
  ) || null;

/**
 * RIPARTIZIONE «LARGEST REMAINDER» (metodo di Hamilton): divide `totale`
 * pezzi fra le taglie in proporzione ai `pesi`, senza perderne nessuno.
 *
 * 1. quota di ogni taglia = totale × peso / sommaPesi;
 * 2. a ciascuna la parte INTERA della sua quota;
 * 3. i pezzi che restano (totale − somma delle parti intere) vanno uno per
 *    uno alle taglie col RESTO più grande; a parità di resto, alla taglia
 *    che viene prima nell'`ordine` dato (deterministico).
 *
 * Aritmetica sui soli interi (`totale × peso` e il resto della divisione
 * intera), così nessun errore di virgola mobile sposta un pezzo.
 * Le taglie a peso zero non ricevono nulla. Torna una mappa con le sole
 * taglie che ricevono almeno un pezzo; a `totale` 0 o pesi nulli, vuota.
 */
export const ripartisciLargestRemainder = (totale, pesi, ordine) => {
  const esito = {};
  const n = Number.isInteger(totale) && totale > 0 ? totale : 0;
  const base = (Array.isArray(ordine) ? ordine : []).filter(
    (t) => Number.isInteger(pesi?.[t]) && pesi[t] > 0
  );
  const sommaPesi = base.reduce((s, t) => s + pesi[t], 0);
  if (n === 0 || sommaPesi === 0) return esito;

  const quote = base.map((t, indice) => {
    const prodotto = n * pesi[t];
    return {
      taglia: t,
      indice,
      intero: Math.floor(prodotto / sommaPesi),
      resto: prodotto % sommaPesi
    };
  });

  let daAssegnare = n - quote.reduce((s, q) => s + q.intero, 0);

  const perResto = quote
    .slice()
    .sort((a, b) => b.resto - a.resto || a.indice - b.indice);

  for (let i = 0; daAssegnare > 0 && i < perResto.length; i += 1) {
    perResto[i].intero += 1;
    daAssegnare -= 1;
  }

  quote.forEach((q) => {
    if (q.intero > 0) esito[q.taglia] = q.intero;
  });

  return esito;
};

/**
 * La stima di UN (squadra × capo) — o di un capo intero, quando `voce` è già
 * la somma: vedi `stimaSommata`. Rende:
 *   { disponibile, stimata, motivo, nonConsegnatePerTaglia, daOrdinarePerTaglia,
 *     nonStimabili, metodo }
 * dove `nonConsegnatePerTaglia` è la ripartizione dei non consegnati (stimata
 * o esatta) e `daOrdinarePerTaglia` = consegnate reali + non consegnate, per
 * taglia. Se `disponibile` è falso le due mappe sono `null`.
 */
const stimaDellaVoce = (voce, capo, taglieNote) => {
  const base = { nonStimabili: 0, metodo: METODO_ARROTONDAMENTO };

  // Capo senza taglia: niente da stimare, tutto è «(no size)», numeri esatti.
  if (!capoHaTaglia(capo)) {
    return {
      ...base,
      disponibile: true,
      stimata: false,
      motivo: "capo-senza-taglia",
      nonConsegnatePerTaglia: senzaZeri({ [SENZA_TAGLIA]: voce.nonConsegnate }),
      daOrdinarePerTaglia: senzaZeri({ [SENZA_TAGLIA]: voce.daConsegnare })
    };
  }

  // Nulla di non consegnato: il «da ordinare» sono le consegne reali.
  if (voce.nonConsegnate === 0) {
    return {
      ...base,
      disponibile: true,
      stimata: false,
      motivo: "nulla-da-stimare",
      nonConsegnatePerTaglia: {},
      daOrdinarePerTaglia: senzaZeri(voce.consegnatePerTaglia)
    };
  }

  const stimate = ripartisciLargestRemainder(
    voce.nonConsegnate,
    voce.consegnatePerTaglia,
    taglieNote
  );

  // Nessuna consegna con taglia nota: la proporzione non esiste, e non si
  // inventa.
  if (Object.keys(stimate).length === 0) {
    return {
      ...base,
      disponibile: false,
      stimata: false,
      motivo: "nessuna-consegna-con-taglia",
      nonConsegnatePerTaglia: null,
      daOrdinarePerTaglia: null,
      nonStimabili: voce.nonConsegnate
    };
  }

  return {
    ...base,
    disponibile: true,
    stimata: true,
    motivo: null,
    nonConsegnatePerTaglia: stimate,
    daOrdinarePerTaglia: senzaZeri(
      sommaIn({ ...voce.consegnatePerTaglia }, stimate)
    )
  };
};

/**
 * La stima di un capo su TUTTE le squadre = la SOMMA delle stime per squadra.
 * Ciò che una squadra non sa stimare finisce in `nonStimabili`, e il totale
 * resta disponibile finché almeno una squadra ha una stima (o non c'è nulla
 * da stimare).
 */
const stimaSommata = (voce, capo, stimeDelleSquadre) => {
  if (!capoHaTaglia(capo) || voce.nonConsegnate === 0) {
    return stimaDellaVoce(voce, capo, []);
  }

  const nonConsegnatePerTaglia = {};
  let nonStimabili = 0;
  let stimata = false;

  stimeDelleSquadre.forEach((stima) => {
    if (!stima.disponibile) {
      nonStimabili += stima.nonStimabili;
      return;
    }
    sommaIn(nonConsegnatePerTaglia, stima.nonConsegnatePerTaglia);
    if (stima.stimata) stimata = true;
  });

  const base = { nonStimabili, metodo: METODO_ARROTONDAMENTO };

  if (nonStimabili >= voce.nonConsegnate) {
    return {
      ...base,
      disponibile: false,
      stimata: false,
      motivo: "nessuna-consegna-con-taglia",
      nonConsegnatePerTaglia: null,
      daOrdinarePerTaglia: null
    };
  }

  return {
    ...base,
    disponibile: true,
    stimata,
    motivo: null,
    nonConsegnatePerTaglia: senzaZeri(nonConsegnatePerTaglia),
    daOrdinarePerTaglia: senzaZeri(
      sommaIn({ ...voce.consegnatePerTaglia }, nonConsegnatePerTaglia)
    )
  };
};

/**
 * IL REPORT.
 *
 * @param {Object} argomenti
 * @param {Array}  argomenti.utenti  l'elenco di `GET /users` com'è in pagina
 * @param {Object} argomenti.config  il catalogo `{ roles, teams, items }`
 *                                   dell'anno guardato (`null` = nessuno)
 * @param {number} argomenti.anno    l'anno guardato («Season Viewed»)
 * @param {Array}  argomenti.sizes   la lista delle taglie della pagina, in
 *                                   ordine (senza stringa vuota dal D9A; se
 *                                   c'è, si toglie)
 * @returns {Object} {
 *   anno, metodo,
 *   capi: [{ id, label, hasSize }],          — i capi attivi, in ordine
 *   taglieNote: [...],                        — le taglie della lista, senza ""
 *   colonneTaglia: [...taglieNote, "", "__other__"],
 *   giocatori, giocatoriSenzaSquadra,
 *   perCapo: { [id]: Voce },                  — il totale generale
 *   perSquadra: [{ squadra, giocatori, perCapo: { [id]: Voce } }]  — per nome
 * }
 * Voce = {
 *   daConsegnare, consegnate, nonConsegnate,
 *   consegnatePerTaglia: { [taglia]: n }      — tutte le colonne, anche a 0
 *   stima: { disponibile, stimata, motivo, nonConsegnatePerTaglia,
 *            daOrdinarePerTaglia, nonStimabili, metodo }
 * }
 * Invarianti: daConsegnare = consegnate + nonConsegnate; la somma di
 * `consegnatePerTaglia` = consegnate; se `stima.disponibile`, la somma di
 * `nonConsegnatePerTaglia` + `nonStimabili` = nonConsegnate e la somma di
 * `daOrdinarePerTaglia` + `nonStimabili` = daConsegnare.
 */
export const reportDivise = ({ utenti, config, anno, sizes } = {}) => {
  const capi = config
    ? capiAttivi(capiDelCatalogo(config.items)).map((capo) => ({
        id: capo.id,
        label: String(capo.label ?? capo.id),
        hasSize: capoHaTaglia(capo)
      }))
    : [];

  const taglieNote = (Array.isArray(sizes) ? sizes : []).filter(
    (s) => typeof s === "string" && s !== SENZA_TAGLIA
  );
  const colonneTaglia = [...taglieNote, SENZA_TAGLIA, ALTRA_TAGLIA];

  const nuovaVoce = () => ({
    daConsegnare: 0,
    consegnate: 0,
    nonConsegnate: 0,
    consegnatePerTaglia: contatore(colonneTaglia)
  });

  const nuovoBlocco = (squadra) => {
    const perCapo = {};
    capi.forEach((capo) => {
      perCapo[capo.id] = nuovaVoce();
    });
    return { squadra, giocatori: 0, perCapo };
  };

  const squadre = new Map();
  let giocatori = 0;
  let giocatoriSenzaSquadra = 0;

  // ⚠️ SENZA UN ANNO NON C'È REPORT. Il predicato dell'anno, con `anno ===
  // null`, lascia passare le tessere attive di TUTTE le annate
  // (`utils/squadre.js`, `tesseraDellAnno`): la pagina lo evita col cancello
  // `stagioneLeggibile`, e qui si ripete la stessa difesa — un report senza
  // anno non conta nessuno, invece di sommare stagioni diverse.
  const elenco = Number.isInteger(anno) && Array.isArray(utenti) ? utenti : [];

  elenco.forEach((utente) => {
    const tessera = tesseraGiocatoreDellAnno(utente?.contexts, anno);
    if (!tessera) return;

    giocatori += 1;

    // La squadra ESATTA della tessera (nome per nome, come la spettanza
    // legge `config.teams[squadra]`): il report raggruppa su quel nome.
    const squadra = primaSquadraDellaTessera(tessera);
    if (squadra === "") {
      giocatoriSenzaSquadra += 1;
      return;
    }

    if (!squadre.has(squadra)) squadre.set(squadra, nuovoBlocco(squadra));
    const blocco = squadre.get(squadra);
    blocco.giocatori += 1;

    // La SOLA tessera PLAYER: la parte di giocatore, e basta.
    const spettanti = tipiDivisaSpettanti([tessera], config);
    // Le consegne DELL'ANNO del report, dalla sede unica (Fetta 3): non più
    // il piatto `utente.uniforms`, che dopo il battesimo è il 2026 fermo.
    const divise = consegneDellAnnata(utente, anno);

    capi.forEach((capo) => {
      if (!spettanti.has(capo.id)) return;

      const voce = blocco.perCapo[capo.id];
      voce.daConsegnare += 1;

      const cella = divise[capo.id];
      if (!cella || cella.status !== STATO_CONSEGNATO) {
        voce.nonConsegnate += 1;
        return;
      }

      voce.consegnate += 1;

      const taglia = capo.hasSize ? String(cella.size ?? "").trim() : SENZA_TAGLIA;
      if (taglia === SENZA_TAGLIA) {
        voce.consegnatePerTaglia[SENZA_TAGLIA] += 1;
      } else if (taglieNote.includes(taglia)) {
        voce.consegnatePerTaglia[taglia] += 1;
      } else {
        voce.consegnatePerTaglia[ALTRA_TAGLIA] += 1;
      }
    });
  });

  // Per squadra, in ordine di nome (confronto a maiuscolo, come la tendina
  // della pagina), con la stima di ogni (squadra × capo).
  const perSquadra = Array.from(squadre.values())
    .sort((a, b) => {
      const ka = a.squadra.toUpperCase();
      const kb = b.squadra.toUpperCase();
      return ka > kb ? 1 : ka < kb ? -1 : 0;
    })
    .map((blocco) => {
      const perCapo = {};
      capi.forEach((capo) => {
        const voce = blocco.perCapo[capo.id];
        perCapo[capo.id] = {
          ...voce,
          stima: stimaDellaVoce(voce, capo, taglieNote)
        };
      });
      return { squadra: blocco.squadra, giocatori: blocco.giocatori, perCapo };
    });

  // Il totale generale per capo: somma delle squadre, stima sommata.
  const perCapo = {};
  capi.forEach((capo) => {
    const totale = nuovaVoce();
    const stime = [];
    perSquadra.forEach((blocco) => {
      const voce = blocco.perCapo[capo.id];
      totale.daConsegnare += voce.daConsegnare;
      totale.consegnate += voce.consegnate;
      totale.nonConsegnate += voce.nonConsegnate;
      sommaIn(totale.consegnatePerTaglia, voce.consegnatePerTaglia);
      stime.push(voce.stima);
    });
    perCapo[capo.id] = {
      ...totale,
      stima: stimaSommata(totale, capo, stime)
    };
  });

  return {
    anno,
    metodo: METODO_ARROTONDAMENTO,
    capi,
    taglieNote,
    colonneTaglia,
    giocatori,
    giocatoriSenzaSquadra,
    perCapo,
    perSquadra
  };
};
